import { Link } from "@tanstack/react-router";
import { ContinueWithGoogleButton } from "@/features/identity/components/continue-with-google-button";
import { rememberConsentReturnTo } from "@/shared/auth/oauth-return-to";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";

interface LoginPasswordFormProps {
  email: string;
  password: string;
  error: string | null;
  pending: boolean;
  /// Set inside the AI app consent screen, so Google sign-in comes back there.
  returnTo?: string;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onUseMagicLink: () => void;
}

/// Primary email + password sign-in form. Purely presentational: the parent
/// route owns the credential state, the mutation, and navigation.
export function LoginPasswordForm({
  email,
  password,
  error,
  pending,
  returnTo,
  onEmailChange,
  onPasswordChange,
  onSubmit,
  onUseMagicLink,
}: LoginPasswordFormProps) {
  // Both links leave the consent URL; the marker lets signup and reset come back.
  const remember = () => {
    if (returnTo) rememberConsentReturnTo(returnTo);
  };
  return (
    <div className="flex flex-col gap-4">
      <ContinueWithGoogleButton label="Sign in with Google" returnTo={returnTo} />
      <div className="flex items-center gap-3 text-2xs uppercase tracking-wider text-text-quaternary">
        <span className="h-px flex-1 bg-border" />
        or
        <span className="h-px flex-1 bg-border" />
      </div>
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            placeholder="you@company.com"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link
              to="/forgot-password"
              search={{}}
              onClick={remember}
              className="text-xs text-text-brand underline-offset-4 hover:underline"
            >
              Forgot password?
            </Link>
          </div>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            placeholder="Your password"
          />
        </div>
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <Button type="submit" disabled={pending}>
          {pending ? "Signing in…" : "Sign in"}
        </Button>
        <button
          type="button"
          className="text-xs text-text-tertiary underline-offset-4 hover:text-text-secondary hover:underline"
          onClick={onUseMagicLink}
        >
          Email me a one-time code instead
        </button>
        <p className="text-xs text-text-tertiary">
          No account?{" "}
          <Link
            to="/signup"
            search={{}}
            onClick={remember}
            className="text-text-brand underline-offset-4 hover:underline"
          >
            Create one
          </Link>
          .
        </p>
      </form>
    </div>
  );
}
